import { useMemo, useState } from 'react';
import { Download, Expand } from 'lucide-react';
import { useOrgChart, useCountries, useDepartments } from '@/lib/queries';
import { initials } from '@isoft-people/utils';

interface Node {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  managerId?: string | null;
  position?: { title: string };
  country?: { code: string; nameEs: string };
  department?: { name: string };
  children?: Node[];
}

function exportOrgCsv(items: any[]) {
  const header = ['Empleado', 'Puesto', 'Departamento', 'Pais', 'Jefe'];
  const byId: Record<string, any> = {};
  items.forEach((i) => (byId[i.id] = i));
  const rows = items.map((i) => {
    const jefe = i.managerId && byId[i.managerId]
      ? `${byId[i.managerId].firstName} ${byId[i.managerId].lastName}`
      : '';
    return [
      `${i.firstName} ${i.lastName}`,
      i.position?.title ?? '',
      i.department?.name ?? '',
      i.country?.nameEs ?? '',
      jefe,
    ];
  });
  const csv = [header, ...rows]
    .map((row) =>
      row
        .map((c) => {
          const s = String(c ?? '');
          return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `organigrama_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildTree(items: Node[]): Node[] {
  const map: Record<string, Node> = {};
  items.forEach((i) => (map[i.id] = { ...i, children: [] }));
  const roots: Node[] = [];
  items.forEach((i) => {
    if (i.managerId && map[i.managerId]) map[i.managerId].children!.push(map[i.id]);
    else roots.push(map[i.id]);
  });
  return roots;
}

const AVATAR_COLORS = [
  'from-primary to-primary-light',
  'from-accent to-green-400',
  'from-warning to-yellow-400',
  'from-error to-pink-400',
  'from-info to-cyan-400',
  'from-purple-500 to-indigo-400',
];

function NodeCard({
  node,
  level = 0,
  compact = false,
}: {
  node: Node;
  level?: number;
  compact?: boolean;
}) {
  const fullName = `${node.firstName} ${node.lastName}`;
  const color = AVATAR_COLORS[level % AVATAR_COLORS.length];
  return (
    <li className="flex flex-col items-center">
      <div
        className={`flex ${
          compact ? 'w-36' : 'w-48'
        } flex-col items-center gap-1 rounded-lg border border-border bg-white p-4 text-center shadow-sm transition hover:border-primary hover:shadow-md`}
      >
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${color} text-sm font-semibold text-white`}
        >
          {initials(fullName)}
        </div>
        <p className="mt-1 text-sm font-semibold text-font-primary">{fullName}</p>
        <p className="text-[11px] text-font-secondary">{node.position?.title}</p>
        {!compact && node.department && (
          <span className="mt-1 rounded-full bg-primary-subtle px-2 py-0.5 text-[10px] font-semibold text-primary">
            {node.department.name}
          </span>
        )}
      </div>
      {node.children && node.children.length > 0 && (
        <>
          <div className="h-4 w-px bg-border" />
          <ul className="flex items-start gap-4 border-t border-border pt-4">
            {node.children.map((c) => (
              <NodeCard key={c.id} node={c} level={level + 1} compact={level >= 1} />
            ))}
          </ul>
        </>
      )}
    </li>
  );
}

export function OrgChartPage() {
  const [countryId, setCountryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const { data, isLoading } = useOrgChart(countryId || undefined);
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();
  const tree = useMemo(() => (data ? buildTree(data.items) : []), [data]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Organigrama</h1>
          <p className="mt-1 text-sm text-font-secondary">
            Estructura jerárquica del equipo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            <option value="">País: Todos</option>
            {countries?.items?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.nameEs}
              </option>
            ))}
          </select>
          <select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            <option value="">Área: Todas</option>
            {departments?.items?.map((d: any) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => exportOrgCsv(data?.items ?? [])}
            className="flex h-9 items-center gap-2 rounded-md border border-border bg-white px-3 text-sm font-semibold text-font-secondary hover:bg-bg"
          >
            <Download size={14} /> Exportar CSV
          </button>
          <button
            onClick={() => window.print()}
            className="flex h-9 items-center gap-2 rounded-md border border-border bg-white px-3 text-sm font-semibold text-font-secondary hover:bg-bg"
          >
            <Expand size={14} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-white p-10 shadow-sm">
        {isLoading ? (
          <p className="py-10 text-center text-font-secondary">Cargando...</p>
        ) : tree.length === 0 ? (
          <p className="py-10 text-center text-font-secondary">
            No hay datos para mostrar.
          </p>
        ) : (
          <ul className="flex flex-wrap items-start justify-center gap-10">
            {tree.map((root) => (
              <NodeCard key={root.id} node={root} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
